// Diagnostic engine — pattern-matches assertion failures against topology
// context to produce actionable, structured diagnostics.
//
// Pure functions. Called after assertions run. Does not modify assertion
// results — it enriches them with diagnosis metadata that the UI renders
// as diagnostic cards.

import type { AssertionDiagnosis } from '../../types/assertion-diagnostics.ts';
import type { AssertionOutput } from './assertionEngine.ts';
import type {
  SimulationTopology,
  FrameDef,
  LaneDef,
  NodeDef,
  BranchPredicate,
} from '../../types/index.ts';

// ---------------------------------------------------------------------------
// Topology context — precomputed lookup structures for fast diagnosis
// ---------------------------------------------------------------------------

export interface TopologyContext {
  /** Node ID → FrameDef it belongs to */
  nodeToFrame: Map<string, FrameDef>;
  /** Node ID → LaneDef it belongs to */
  nodeToLane: Map<string, LaneDef>;
  /** Node ID → NodeDef */
  nodesById: Map<string, NodeDef>;
  /** Node ID → BranchPredicate (if any) */
  predicatesByNode: Map<string, BranchPredicate>;
  /** Event routing map */
  eventRouting: Record<string, string[]>;
}

/** Build a TopologyContext from a loaded SimulationTopology. */
export function buildTopologyContext(topology: SimulationTopology): TopologyContext {
  const nodeToFrame = new Map<string, FrameDef>();
  const nodeToLane = new Map<string, LaneDef>();
  const nodesById = new Map<string, NodeDef>();
  const lanesById = new Map<string, LaneDef>();

  for (const lane of topology.lanes) {
    lanesById.set(lane.id, lane);
  }

  for (const frame of topology.frames) {
    for (const node of frame.nodes) {
      nodeToFrame.set(node.id, frame);
      nodesById.set(node.id, node);
      const lane = lanesById.get(node.lane);
      if (lane) nodeToLane.set(node.id, lane);
    }
  }

  const predicatesByNode = new Map<string, BranchPredicate>();
  for (const [nodeId, pred] of Object.entries(topology.branchPredicates)) {
    predicatesByNode.set(nodeId, pred);
  }

  return {
    nodeToFrame,
    nodeToLane,
    nodesById,
    predicatesByNode,
    eventRouting: topology.eventRouting,
  };
}

// ---------------------------------------------------------------------------
// Helper — clean up node IDs for display
// ---------------------------------------------------------------------------

function displayNodeId(nodeId: string): string {
  // Use the node name after the last "::" qualifier, then strip synthetic prefixes.
  const qualifierIndex = nodeId.lastIndexOf('::');
  const displayId = qualifierIndex >= 0 ? nodeId.slice(qualifierIndex + 2) : nodeId;

  return displayId.replace(/^moment-\d+-/i, '').replace(/^n\d+-/, '');
}

function frameMomentName(frame: FrameDef | undefined): string | null {
  if (!frame) return null;
  return frame.moment || frame.id.replace(/^moment-\d+-/i, '');
}

// ---------------------------------------------------------------------------
// Path assertion diagnostics
// ---------------------------------------------------------------------------

/** Diagnose a failed path-step assertion. */
export function diagnosePathStep(
  assertion: AssertionOutput,
  expectedPath: string[],
  actualNodeId: string,
  stepIndex: number,
  ctx: TopologyContext,
): AssertionDiagnosis {
  const expectedNodeId = expectedPath[stepIndex];

  // Case 1: Beyond expected path length
  if (stepIndex >= expectedPath.length) {
    const actualFrame = ctx.nodeToFrame.get(actualNodeId);
    const actualLane = ctx.nodeToLane.get(actualNodeId);
    return {
      summary: 'Simulation walked past the expected path',
      explanation:
        `The simulation produced ${stepIndex + 1} steps but the scenario only expected ` +
        `${expectedPath.length}. This usually means an extra event was emitted — check ` +
        `whether a saga or policy in your spec is producing an unexpected downstream event.`,
      sourceHint: actualFrame
        ? `Look at the "${frameMomentName(actualFrame)}" moment — it produced an event that wasn't expected in this scenario.`
        : null,
      comparison: {
        expected: `${expectedPath.length} steps (path ends)`,
        actual: `Step ${stepIndex + 1}: ${displayNodeId(actualNodeId)}`,
        label: 'Path length',
      },
      severity: 'error',
      frameName: frameMomentName(actualFrame),
      laneName: actualLane?.label ?? null,
    };
  }

  // Case 2: Node mismatch — the most common failure
  if (!expectedNodeId) {
    return fallbackDiagnosis(assertion);
  }

  const expectedFrame = ctx.nodeToFrame.get(expectedNodeId);
  const actualFrame = ctx.nodeToFrame.get(actualNodeId);
  const actualLane = ctx.nodeToLane.get(actualNodeId);
  const expectedNode = ctx.nodesById.get(expectedNodeId);

  // Sub-case 2a: Different frames — likely a branch routing issue
  if (expectedFrame && actualFrame && expectedFrame.id !== actualFrame.id) {
    // Check if the actual node's frame is a branch frame
    if (actualFrame.isBranch) {
      return {
        summary: 'Event routed to a branch path instead of the expected path',
        explanation:
          `At step ${stepIndex}, the simulation routed to the "${frameMomentName(actualFrame)}" ` +
          `branch instead of continuing to "${frameMomentName(expectedFrame)}". This happens ` +
          `when a branch predicate evaluates differently than the scenario expects. Check the ` +
          `branch condition — the field value in the event payload may not match the expected route.`,
        sourceHint:
          `Review the branch predicate in the "${frameMomentName(actualFrame)}" moment. ` +
          `The routing condition determines which path the simulation follows.`,
        comparison: {
          expected: `${displayNodeId(expectedNodeId)} (${frameMomentName(expectedFrame)})`,
          actual: `${displayNodeId(actualNodeId)} (${frameMomentName(actualFrame)})`,
          label: 'Node at step ' + stepIndex,
        },
        severity: 'error',
        frameName: frameMomentName(actualFrame),
        laneName: actualLane?.label ?? null,
      };
    }

    // Sub-case 2b: Different frames, neither is a branch — wrong moment order
    return {
      summary: 'Event arrived at the wrong moment',
      explanation:
        `At step ${stepIndex}, the simulation reached "${frameMomentName(actualFrame)}" ` +
        `but expected "${frameMomentName(expectedFrame)}". The event routing in the topology ` +
        `sent the event to a different moment than the scenario anticipated. This can happen ` +
        `when the event type is routed to multiple targets or when the topology connections ` +
        `changed since the scenario was generated.`,
      sourceHint:
        `Check the event routing for this event type in your spec. If you recently changed ` +
        `connections between moments, regenerate the scenario to match the updated topology.`,
      comparison: {
        expected: `${displayNodeId(expectedNodeId)} in "${frameMomentName(expectedFrame)}"`,
        actual: `${displayNodeId(actualNodeId)} in "${frameMomentName(actualFrame)}"`,
        label: 'Moment at step ' + stepIndex,
      },
      severity: 'error',
      frameName: frameMomentName(actualFrame),
      laneName: actualLane?.label ?? null,
    };
  }

  // Sub-case 2c: Same frame, different node — event resolved to wrong node within the moment
  if (expectedFrame && actualFrame && expectedFrame.id === actualFrame.id) {
    return {
      summary: 'Event resolved to the wrong node within the same moment',
      explanation:
        `At step ${stepIndex}, the event resolved to "${displayNodeId(actualNodeId)}" ` +
        `instead of "${displayNodeId(expectedNodeId)}" within the "${frameMomentName(actualFrame)}" ` +
        `moment. Both nodes are in the same moment, so the event routing or node ID mapping ` +
        `may be incorrect. Check that the event type maps to the correct node.`,
      sourceHint:
        `In the "${frameMomentName(actualFrame)}" moment, verify that the event type in ` +
        `the scenario maps to the correct ${expectedNode?.type ?? 'node'} node.`,
      comparison: {
        expected: displayNodeId(expectedNodeId),
        actual: displayNodeId(actualNodeId),
        label: 'Node in ' + frameMomentName(actualFrame),
      },
      severity: 'error',
      frameName: frameMomentName(actualFrame),
      laneName: actualLane?.label ?? null,
    };
  }

  // Sub-case 2d: Can't determine frame context — generic mismatch
  return {
    summary: 'Path mismatch — expected a different node',
    explanation:
      `At step ${stepIndex}, the simulation visited "${displayNodeId(actualNodeId)}" ` +
      `but the scenario expected "${displayNodeId(expectedNodeId)}". Regenerate the ` +
      `scenario if you've changed the topology, or check the event routing table.`,
    sourceHint: null,
    comparison: {
      expected: displayNodeId(expectedNodeId),
      actual: displayNodeId(actualNodeId),
      label: 'Node at step ' + stepIndex,
    },
    severity: 'error',
    frameName: null,
    laneName: null,
  };
}

/** Diagnose a failed path-sequence assertion. */
export function diagnosePathSequence(
  _assertion: AssertionOutput,
  actualPath: string[],
  expectedPath: string[],
  ctx: TopologyContext,
): AssertionDiagnosis {
  // Find divergence point
  let divergeIndex = 0;
  while (
    divergeIndex < actualPath.length &&
    divergeIndex < expectedPath.length &&
    actualPath[divergeIndex] === expectedPath[divergeIndex]
  ) {
    divergeIndex++;
  }

  // Length mismatch after matching prefix
  if (divergeIndex === Math.min(actualPath.length, expectedPath.length)) {
    const longer = actualPath.length > expectedPath.length ? 'actual' : 'expected';
    return {
      summary:
        longer === 'actual'
          ? 'Simulation produced extra steps beyond the expected path'
          : 'Simulation ended before completing the expected path',
      explanation:
        longer === 'actual'
          ? `The first ${divergeIndex} steps matched, but the simulation continued for ` +
            `${actualPath.length - expectedPath.length} additional steps. A saga or policy ` +
            `may be emitting unexpected downstream events.`
          : `The first ${divergeIndex} steps matched, but the simulation stopped ` +
            `${expectedPath.length - actualPath.length} steps short. An event may have been ` +
            `dropped or a precondition failed, preventing further processing.`,
      sourceHint:
        longer === 'actual'
          ? 'Check saga and policy definitions for unexpected event emissions after the main flow completes.'
          : 'Check whether a precondition or guard clause is stopping the flow early.',
      comparison: {
        expected: `${expectedPath.length} steps`,
        actual: `${actualPath.length} steps`,
        label: 'Total path length',
      },
      severity: 'error',
      frameName: null,
      laneName: null,
    };
  }

  // Divergence at a specific node
  const expectedNodeId = expectedPath[divergeIndex];
  const actualNodeId = actualPath[divergeIndex];
  const expectedFrame = ctx.nodeToFrame.get(expectedNodeId);
  const actualFrame = actualNodeId ? ctx.nodeToFrame.get(actualNodeId) : undefined;

  return {
    summary: `Path diverged at step ${divergeIndex}`,
    explanation:
      `The simulation followed the expected path for ${divergeIndex} steps, then ` +
      `diverged. Expected "${displayNodeId(expectedNodeId)}"` +
      (expectedFrame ? ` in "${frameMomentName(expectedFrame)}"` : '') +
      ` but got "${actualNodeId ? displayNodeId(actualNodeId) : 'END'}"` +
      (actualFrame ? ` in "${frameMomentName(actualFrame)}"` : '') +
      `. This is the root divergence — all subsequent steps follow from this point.`,
    sourceHint: expectedFrame
      ? `Start debugging at step ${divergeIndex} in the "${frameMomentName(expectedFrame)}" moment. ` +
        `Check event routing, branch predicates, and connection targets around this point.`
      : `Start debugging at step ${divergeIndex}. Check the event routing for the event at this position.`,
    comparison: {
      expected: `${displayNodeId(expectedNodeId)}${expectedFrame ? ' (' + frameMomentName(expectedFrame) + ')' : ''}`,
      actual: `${actualNodeId ? displayNodeId(actualNodeId) : 'END'}${actualFrame ? ' (' + frameMomentName(actualFrame) + ')' : ''}`,
      label: 'Divergence at step ' + divergeIndex,
    },
    severity: 'error',
    frameName: frameMomentName(expectedFrame),
    laneName: null,
  };
}

// ---------------------------------------------------------------------------
// Fallback
// ---------------------------------------------------------------------------

function fallbackDiagnosis(assertion: AssertionOutput): AssertionDiagnosis {
  return {
    summary: 'Assertion failed',
    explanation: assertion.detail,
    sourceHint: null,
    comparison: null,
    severity: 'error',
    frameName: null,
    laneName: null,
  };
}
